import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client.ts';
import type {
  AuthStatus,
  Brand,
  DestroyStatus,
  DevicesResponse,
  EnvInfo,
  PiiResponse,
  PiiStatus,
  Profile,
  ProfileEdit,
  SignaturePayload,
  SignatureRecord,
  Subscription,
} from './types.ts';

export const keys = {
  auth: ['auth'] as const,
  brand: ['brand'] as const,
  env: ['env'] as const,
  devices: ['devices'] as const,
  pii: ['pii'] as const,
  piiStatus: ['pii', 'status'] as const,
  subscription: ['subscription'] as const,
  signature: ['signature'] as const,
  destroyStatus: ['destroy', 'status'] as const,
  profile: ['profile'] as const,
};

export function useProfile() {
  return useQuery({ queryKey: keys.profile, queryFn: () => api.get<Profile>('/api/profile') });
}

export function useSaveProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (edit: ProfileEdit) => api.put<Profile>('/api/profile', edit),
    onSuccess: (profile) => client.setQueryData(keys.profile, profile),
  });
}

export function useAuthStatus() {
  return useQuery({ queryKey: keys.auth, queryFn: () => api.get<AuthStatus>('/api/auth-status'), retry: false, staleTime: 5 * 60_000 });
}

export function useBrand() {
  return useQuery({ queryKey: keys.brand, queryFn: () => api.get<Brand>('/api/brand'), staleTime: Infinity });
}

export function useEnv() {
  return useQuery({ queryKey: keys.env, queryFn: () => api.get<EnvInfo>('/api/env'), staleTime: Infinity });
}

export function useDevices() {
  return useQuery({ queryKey: keys.devices, queryFn: () => api.get<DevicesResponse>('/api/devices') });
}

export function usePii() {
  return useQuery({ queryKey: keys.pii, queryFn: () => api.get<PiiResponse>('/api/account/pii') });
}

export function usePiiStatus() {
  return useQuery({ queryKey: keys.piiStatus, queryFn: () => api.get<PiiStatus>('/api/account/pii/status') });
}

export function useSubscription() {
  return useQuery({ queryKey: keys.subscription, queryFn: () => api.get<Subscription>('/api/subscription') });
}

/** The stored signature record, or null when the account has none yet (the server answers 204). */
export function useSignature() {
  return useQuery({
    queryKey: keys.signature,
    queryFn: async (): Promise<SignatureRecord | null> => (await api.get<SignatureRecord | undefined>('/api/signature')) ?? null,
    staleTime: Infinity,
  });
}

export function useSaveSignature() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: SignaturePayload) => api.put<{ updatedAt: number }>('/api/signature', payload),
    onSuccess: (_answer, payload) => {
      client.setQueryData<SignatureRecord | null>(keys.signature, (current) =>
        current ? { ...current, ...payload, updatedAt: Date.now() } : current,
      );
    },
  });
}

export function useDeleteShare() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (url: string) => api.delete<{ result: string; url: string }>('/api/account/share', { url }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.pii }),
  });
}

export function useDestroyAccount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<DestroyStatus>('/api/account/destroy'),
    onSettled: () => client.invalidateQueries({ queryKey: keys.piiStatus }),
  });
}
